/* <gr-quantity> — a real <input type="number"> with real <button> controls,
   visually restyled. Debounces network traffic and refuses to emit a change
   the input's own min/max would reject, so rapid tapping cannot produce an
   invalid cart state or a queue of doomed requests. */

class GrQuantity extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.input = this.querySelector('input[type="number"]');
    this.minus = this.querySelector('[data-qty-minus]');
    this.plus = this.querySelector('[data-qty-plus]');
    this.timer = null;

    this.onStep = this.onStep.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onBlur = this.onBlur.bind(this);

    if (this.minus) this.minus.addEventListener('click', this.onStep);
    if (this.plus) this.plus.addEventListener('click', this.onStep);
    if (this.input) {
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('blur', this.onBlur);
    }
    this.syncButtons();
  }

  disconnectedCallback() {
    if (this.timer) clearTimeout(this.timer);
    if (this.minus) this.minus.removeEventListener('click', this.onStep);
    if (this.plus) this.plus.removeEventListener('click', this.onStep);
    if (this.input) {
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('blur', this.onBlur);
    }
    this._bound = false;
  }

  get min() { return Number(this.input.min || 0); }
  get max() { return this.input.max === '' ? Infinity : Number(this.input.max); }

  clamp(value) { return Math.min(this.max, Math.max(this.min, value)); }

  syncButtons() {
    if (!this.input) return;
    const value = Number(this.input.value);
    if (this.minus) this.minus.disabled = value <= this.min;
    if (this.plus) this.plus.disabled = value >= this.max;
  }

  onStep(event) {
    event.preventDefault();
    const step = event.currentTarget.hasAttribute('data-qty-plus') ? 1 : -1;
    const next = this.clamp(Number(this.input.value) + step);
    if (next === Number(this.input.value)) return;
    this.input.value = next;
    this.syncButtons();
    this.emit(next, 250);
  }

  onInput() {
    this.syncButtons();
    // Typing gets a longer debounce than tapping: a shopper mid-way through
    // "12" should not have "1" committed underneath them.
    this.emit(null, 700);
  }

  onBlur() {
    const value = this.clamp(Number(this.input.value) || this.min);
    if (value !== Number(this.input.value)) {
      this.input.value = value;
      this.syncButtons();
      this.emit(value, 0);
    }
  }

  emit(value, delay) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      const quantity = this.clamp(value === null ? Number(this.input.value) || this.min : value);
      this.input.value = quantity;
      this.syncButtons();
      this.dispatchEvent(new CustomEvent('gr:quantity:change', {
        bubbles: true,
        detail: { key: this.getAttribute('data-line-key'), quantity: quantity }
      }));
    }, delay);
  }
}

if (!customElements.get('gr-quantity')) customElements.define('gr-quantity', GrQuantity);
